import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Field } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { http } from 'core/ApiClient';

import { FormFieldsContainer } from 'layouts/containers/FormFieldsContainer';
import { SelectField, Option, TextField } from 'components/FormFields';
import { Routes } from 'types/types';
import { selectCountry } from '../selectors';
import { setCountry } from '../reducer/myCompanyReducer';
import {
	selectAllCountriesIdNamePhoneCode,
	addCountries,
	CountriesListAPI,
	setCurrentPage,
	selectCurrentPage,
} from 'features/Country';
import { MyPartnerStateFields } from '../types';
import { ListPaginationConfig } from 'core/constants';

export const Address: React.FC = () => {
	const intl = useIntl();
	const dispatch = useDispatch();
	const [debouncedValue, setDebouncedValue] = useState<string | null>(null);
	const [value, setValue] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const currentCountriesPage = useSelector(selectCurrentPage);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), 500);
		return () => clearTimeout(timer);
	}, [value]);

	useEffect(() => {
		const controller = new AbortController();

		setIsLoading(true);
		if (debouncedValue) {
			http
				.get<CountriesListAPI>(
					`${Routes.Countries}?itemsPerPage=10&title=${debouncedValue}`,
					ListPaginationConfig
				)
				.then((res) => {
					dispatch(addCountries(res.data));
					setIsLoading(false);
				})
				.catch((e: Error) => {
					console.error(e);
					setIsLoading(false);
				});
		}

		return () => controller.abort();
	}, [debouncedValue]);

	const handleCountrySearchValue = (searchVal: string) => {
		setValue(searchVal);
	};

	const countries = useSelector(selectAllCountriesIdNamePhoneCode);

	const country = useSelector(selectCountry);

	const handleCountrySelect = (option: Option) => {
		if (country?.id === option?.id) return;

		dispatch(setCountry(!option ? null : option));
	};

	const handleLoadMoreCountries = () => {
		setIsLoading(true);
		http
			.get<CountriesListAPI>(
				`${Routes.Countries}?itemsPerPage=10&page=${currentCountriesPage + 1}`,
				ListPaginationConfig
			)
			.then((res) => {
				dispatch(setCurrentPage({ currentPage: res.meta.currentPage }));
				dispatch(addCountries(res.data));
			})
			.catch((e: Error) => {
				console.error(e);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<FormFieldsContainer>
			<Field
				as={TextField}
				name={MyPartnerStateFields.addressLine1}
				type="text"
				label={intl.formatMessage({
					id: 'my.company.view.edit.address.line.1',
				})}
			/>
			<Field
				as={TextField}
				name={MyPartnerStateFields.addressLine2}
				type="text"
				label={intl.formatMessage({
					id: 'my.company.view.edit.address.line.2',
				})}
			/>
			<Field
				as={SelectField}
				options={countries}
				value={country}
				onChange={handleCountrySelect}
				name={MyPartnerStateFields.country}
				type="object"
				label={intl.formatMessage({
					id: 'my.company.view.edit.country',
				})}
				onInputChange={handleCountrySearchValue}
				loading={isLoading}
				handleLoadMore={handleLoadMoreCountries}
			/>
			<Field
				as={TextField}
				name={MyPartnerStateFields.city}
				type="text"
				label={intl.formatMessage({
					id: 'my.company.view.edit.city',
				})}
			/>
			<Field
				as={TextField}
				name={MyPartnerStateFields.postalCode}
				type="text"
				label={intl.formatMessage({
					id: 'my.company.view.edit.postal.code',
				})}
			/>
		</FormFieldsContainer>
	);
};
